import axios from 'axios';
import { microServicesRoutes } from '@constants';
import { authApi } from '@lib/apiClients';
import {
  type CredentialsDto,
  type AuthenticationResponse,
  type AuthApiResponse,
  type AuthEnvelope,
  type BackupCode,
  type TotpConfirmation,
  type TotpDisableInput,
  type TotpDisabled,
  type TotpEnrollment,
  type TotpStatus,
  type UserDataResponse,
  type LogoutResponse,
  type ApiResponse,
  type ResetPasswordDto, type AdminResetPasswordDto,
} from '@types';
import { logError } from '@lib/logger/logError';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';

export default function useAuthApi (): {
  authLogin: (
    credentials: CredentialsDto,
  ) => Promise<ApiResponse<AuthenticationResponse>>;
  logout: () => Promise<ApiResponse<LogoutResponse>>;
  refresh: () => Promise<ApiResponse<AuthenticationResponse>>;
  getMe: () => Promise<ApiResponse<UserDataResponse>>;
  resetPasswordAsync: (body: ResetPasswordDto, token: string) => Promise<ApiResponse<AuthApiResponse>>;
  adminResetPasswordAsync: (payload: AdminResetPasswordDto)=> Promise<ApiResponse<AuthApiResponse>>;
  getTotpStatus: () => Promise<ApiResponse<TotpStatus>>;
  startTotpEnrollment: (password: string) => Promise<ApiResponse<TotpEnrollment>>;
  confirmTotpEnrollment: (code: string) => Promise<ApiResponse<TotpConfirmation>>;
  viewBackupCodes: (password: string) => Promise<ApiResponse<BackupCode[]>>;
  regenerateBackupCodes: (password: string) => Promise<ApiResponse<string[]>>;
  disableTotp: (input: TotpDisableInput) => Promise<ApiResponse<TotpDisabled>>;
  } {
  /**
   * What the auth service answered, without the request. The axios error
   * itself is never logged: serialized, it carries the request body
   * (passwords, two-factor codes) and the Authorization header.
   */
  const describeError = (
    error: unknown,
  ): { status?: number; code?: string; serverMessage?: string; retryAfterMinutes?: number } => {
    if (!axios.isAxiosError(error)) {
      return {};
    }

    const body = error.response?.data as
      | { code?: unknown; message?: unknown; retry_after_minutes?: unknown }
      | undefined;

    return {
      status: error.response?.status,
      code: typeof body?.code === 'string' ? body.code : undefined,
      serverMessage: typeof body?.message === 'string' ? body.message : undefined,
      retryAfterMinutes: typeof body?.retry_after_minutes === 'number' ? body.retry_after_minutes : undefined,
    };
  };

  const handleError = (
    message: string,
    error: unknown,
    source: string,
  ): ApiResponse<never> => {
    const errorMessage = extractErrorMessage(error);
    const { status, code, serverMessage, retryAfterMinutes } = describeError(error);

    // Being asked for the two-factor code is a step of the login, not an error.
    if (code !== 'totp_required') {
      void logError(
        message,
        source,
        {
          error: errorMessage,
          status,
          code,
          serverMessage,
        },
        false,
      );
    }

    return {
      success: false,
      error: {
        message,
        status,
        code,
        retryAfterMinutes,
        raw: errorMessage,
      },
    };
  };

  const authLogin = async (
    credentials: CredentialsDto,
  ): Promise<ApiResponse<AuthenticationResponse>> => {
    try {
      const response = await authApi.post<AuthApiResponse>(
        microServicesRoutes.auth.post.auth.login,
        credentials,
      );

      // Auth verifies the credentials and obtains the edge session token over
      // the sealed channel, so this single call yields both tokens
      // (tendril-connect#2224).
      const { access_token: accessToken, edge_token: edgeToken } = response.data;

      if (accessToken == null || typeof accessToken !== 'string') {
        return {
          success: false,
          error: { message: 'access_token not received from backend' },
        };
      }

      if (edgeToken == null || typeof edgeToken !== 'string') {
        return {
          success: false,
          error: { message: 'edge_token not received from backend' },
        };
      }

      return {
        success: true,
        data: { access_token: accessToken, edge_token: edgeToken, status: 'ok' },
      };
    } catch (e) {
      return handleError('Error during Auth login.', e, 'Auth login');
    }
  };

  const logout = async (): Promise<ApiResponse<LogoutResponse>> => {
    try {
      const response = await authApi.get<LogoutResponse>(
        microServicesRoutes.auth.get.auth.logout,
      );
      return { success: true, data: response.data };
    } catch (e) {
      return handleError('Error during logout.', e, 'logout');
    }
  };

  const refresh = async (): Promise<ApiResponse<AuthenticationResponse>> => {
    if (window.location.pathname === '/login') {
      return { success: false };
    }

    try {
      const response = await authApi.get<AuthApiResponse>(
        microServicesRoutes.auth.get.auth.refreshSession,
      );

      const token = response.data.access_token;

      if (token == null || typeof token !== 'string') {
        return { success: false };
      }

      return { success: true, data: { access_token: token, status: 'ok' } };
    } catch (e) {
      return handleError('Error refreshing session.', e, 'refresh');
    }
  };

  const getMe = async (): Promise<ApiResponse<UserDataResponse>> => {
    try {
      const response = await authApi.get<UserDataResponse>(
        microServicesRoutes.auth.get.auth.getMe,
      );
      return { success: true, data: response.data };
    } catch (e) {
      return handleError('Error validating session.', e, 'getMe');
    }
  };

  const resetPasswordAsync = async (
    body: ResetPasswordDto,
    token: string
  ): Promise<ApiResponse<AuthApiResponse>> => {
    try {
      const postConfig = {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      };

      const updatedPass = await authApi.post<AuthApiResponse>(
        microServicesRoutes.auth.post.auth.resetPassword,
        body,
        postConfig
      );
      return { success: true, data: updatedPass.data as Record<string, string> };
    } catch (e) {
      return handleError('Can not reset password.', e, 'resetPassword');
    }
  };

  const adminResetPasswordAsync = async (payload: AdminResetPasswordDto): Promise<ApiResponse<AuthApiResponse>> =>{
    try {
      const { adminEmail, userEmail, adminPassword: password, newPassword } = payload;

      const adminAuthenticationCheck = await authLogin({ email: adminEmail, password });

      if (adminAuthenticationCheck.data == null) {
        return handleError(
          'Could not authenticate Admin.',
          { error: adminAuthenticationCheck.error },
          adminResetPasswordAsync.name,
        );
      }
      const url = `${microServicesRoutes.auth.post.auth.adminResetPassword}`;

      const resetPassBody = { email: userEmail, new_password: newPassword };
      const postConfig = {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      };

      const { data } = await authApi.post<AuthApiResponse>(url, resetPassBody, postConfig);
      
      return { data, success: true };
    } catch (e) {
      return handleError(
        'Could not reset password by Admin.',
        e,
        adminResetPasswordAsync.name,
      );
    }
  };

  const getTotpStatus = async (): Promise<ApiResponse<TotpStatus>> => {
    try {
      const response = await authApi.get<AuthEnvelope<TotpStatus>>(
        microServicesRoutes.auth.get.auth.totpStatus,
      );
      return { success: true, data: response.data.data };
    } catch (e) {
      return handleError('Error reading two-factor status.', e, 'getTotpStatus');
    }
  };

  const startTotpEnrollment = async (
    password: string,
  ): Promise<ApiResponse<TotpEnrollment>> => {
    try {
      const response = await authApi.post<AuthEnvelope<TotpEnrollment>>(
        microServicesRoutes.auth.post.auth.totpEnroll,
        { password },
      );
      return { success: true, data: response.data.data };
    } catch (e) {
      return handleError('Error starting two-factor setup.', e, 'startTotpEnrollment');
    }
  };

  const confirmTotpEnrollment = async (
    code: string,
  ): Promise<ApiResponse<TotpConfirmation>> => {
    try {
      const response = await authApi.post<AuthEnvelope<TotpConfirmation>>(
        microServicesRoutes.auth.post.auth.totpConfirm,
        { code },
      );
      return { success: true, data: response.data.data };
    } catch (e) {
      return handleError('Error confirming two-factor setup.', e, 'confirmTotpEnrollment');
    }
  };

  const viewBackupCodes = async (
    password: string,
  ): Promise<ApiResponse<BackupCode[]>> => {
    try {
      const response = await authApi.post<AuthEnvelope<{ backup_codes: BackupCode[] }>>(
        microServicesRoutes.auth.post.auth.totpBackupCodesView,
        { password },
      );
      return { success: true, data: response.data.data.backup_codes };
    } catch (e) {
      return handleError('Error reading backup codes.', e, 'viewBackupCodes');
    }
  };

  const regenerateBackupCodes = async (
    password: string,
  ): Promise<ApiResponse<string[]>> => {
    try {
      const response = await authApi.post<AuthEnvelope<{ backup_codes: string[] }>>(
        microServicesRoutes.auth.post.auth.totpBackupCodesRegenerate,
        { password },
      );
      return { success: true, data: response.data.data.backup_codes };
    } catch (e) {
      return handleError('Error generating backup codes.', e, 'regenerateBackupCodes');
    }
  };

  const disableTotp = async (
    input: TotpDisableInput,
  ): Promise<ApiResponse<TotpDisabled>> => {
    try {
      const response = await authApi.post<AuthEnvelope<TotpDisabled>>(
        microServicesRoutes.auth.post.auth.totpDisable,
        {
          password: input.password,
          totp_code: input.totpCode,
          backup_code: input.backupCode,
        },
      );
      return { success: true, data: response.data.data };
    } catch (e) {
      return handleError('Error turning two-factor off.', e, 'disableTotp');
    }
  };

  return {
    authLogin,
    logout,
    refresh,
    getMe,
    resetPasswordAsync,
    adminResetPasswordAsync,
    getTotpStatus,
    startTotpEnrollment,
    confirmTotpEnrollment,
    viewBackupCodes,
    regenerateBackupCodes,
    disableTotp,
  };
}
